import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AxiosResponse } from 'axios';
import { axios, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, clearTokens, setCurrentTenantId } from '@/lib/axios';
import { User } from '@/lib/api-client';
import { getStoredClientLocation, isLocationDenied } from '@/lib/client-geolocation';

// Types
interface LoginRequest {
  username: string;
  password: string;
  latitude?: number;
  longitude?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => ({} as User),
  logout: async () => {},
  refreshUser: async () => {},
  switchTenant: async () => {},
  hasPermission: () => false,
});

// Provider props type
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing token and validate on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // Validate token by making a request to get current user
        const response: AxiosResponse<{ success: boolean; data: User }> = await axios.get('/api/Auth/me');
        
        if (response.data.success) {
          const userData = response.data.data;
          setUser(userData);
          const tenantId = userData.currentTenantId ?? userData.tenants?.[0]?.id ?? '';
          if (tenantId) setCurrentTenantId(tenantId);
        } else {
          throw new Error('Failed to get user data');
        }
      } catch (err) {
        // If token is invalid, clear auth data and redirect to login
        clearTokens();
        setError('Your session has expired. Please log in again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginRequest): Promise<User> => {
    if (isLocationDenied()) {
      window.location.href = '/location-required';
      throw new Error('Location permission required');
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const clientLocation = getStoredClientLocation();
      const payload = clientLocation ? { ...credentials, clientLocation } : credentials;
      const response = await axios.post<{ success: boolean; data: { token: string; refreshToken: string; user: User } }>('/api/Auth/login', payload);
      
      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        
        // Store tokens
        setAccessToken(token);
        setRefreshToken(refreshToken);
        const tenantId = user.currentTenantId ?? user.tenants?.[0]?.id ?? '';
        if (tenantId) setCurrentTenantId(tenantId);
        
        // Set user data
        setUser(user);
        
        // Return user data for immediate use
        return user;
      } else {
        throw new Error('Login failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Invalid username or password. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = getAccessToken();
      if (token) {
        await axios.post('/api/Auth/logout', { token });
      }
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout properly. Please try again.');
    } finally {
      // Always clear auth data and redirect to login
      clearTokens();
      setUser(null);
      setIsLoading(false);
      window.location.href = '/sign-in';
    }
  };

  // Switch tenant
  const switchTenant = async (tenantId: string) => {
    try {
      const response = await axios.post<{ success: boolean; data: { token: string; refreshToken: string; user: User } }>('/api/Auth/switch-tenant', { tenantId });
      if (response.data.success && response.data.data) {
        const { token, refreshToken, user } = response.data.data;
        setAccessToken(token);
        setRefreshToken(refreshToken);
        setCurrentTenantId(tenantId);
        setUser(user);
        window.location.reload(); // Reload để clear state và load dữ liệu tenant mới
      } else {
        throw new Error('Chuyển tenant thất bại');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Chuyển tenant thất bại';
      throw new Error(errorMessage);
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const response: AxiosResponse<{ success: boolean; data: User }> = await axios.get('/api/Auth/me');
      
      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  // Helper to check for user permissions
  const hasPermission = (permissionName: string) => {
    if (!user || !user.permissions) return false;
    return user.permissions.some(p => p.name === permissionName);
  };

  // Auth context value
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
    switchTenant,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext; 